import { ICoordinates, IParagraph, IParagraphReferences } from "Common/Interfaces/Domain/References";
import { useCallback, useEffect, useState } from "react";
import { useGetParagraphReferencesQuery } from "Services/References";
import { PDFDocument, rgb } from "pdf-lib";
import { IReference } from "Common/Interfaces";
import { useGetReferencesQuery } from "Services/Conversations";
import { IRectangle } from "Common/Interfaces/Domain/References/IRectangle";
import { useTheme } from "@mui/material";
import { useMatchDownMd } from "Common/Hooks";
import { IReferences } from "../Interfaces";

export const useReferences = (props: IReferences) => {
  const {
    activeReferenceUrl,
    conversationId,
    isCollapsedReference,
    pageNumber: pageNum,
    paragraphs: newParagraph,
    isConversationLoading,
    setIsCollapsedReference,
    pageResetKey,
  } = props;
  const [fileId, setFileId] = useState<string | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [paragraphs, setParagraphs] = useState<string | undefined>(undefined);
  //const [viewUrl, setViewUrl] = useState<string | undefined>(undefined); // use this to find the refernces object
  const matchDownMd = useMatchDownMd();
  const [viewError, setViewError] = useState<boolean>(false);
  const [activeParagraphReference, setActiveParagraphReference] = useState<IParagraphReferences | undefined>(undefined);
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined);
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(false);
  const theme = useTheme();
  const [isReferenceLoading, setIsReferenceLoading] = useState<boolean>(false);

  const [references, setReferences] = useState<IReference[] | undefined>(undefined);
  const [selectedReference, setSelectedReference] = useState<IReference | undefined>(undefined);
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>(undefined);
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState<string | undefined>(selectedReference?.fileUrl); // state for updated PDF URL
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [warningMessage, setWarningMessage] = useState<string | undefined>(undefined);

  const INCH_TO_POINTS = 72;

  const { data, isLoading, isError } = useGetParagraphReferencesQuery(
    { fileId, pageNumber, paragraphs },
    {
      skip:
        fileId === undefined ||
        paragraphs === undefined ||
        pageNumber === undefined ||
        pageNumber === 0 ||
        isDocumentLoading,
    },
  );

  const {
    data: referencesList,
    isLoading: isReferencesListLoading,
    isFetching: isReferencesListFetching,
    error: referencesError,
    refetch,
  } = useGetReferencesQuery(activeConversationId ?? "", {
    skip: activeConversationId === "" || isConversationLoading, // Skip the query when conversationId is empty
  });

  useEffect(() => {
    if (!isReferencesListLoading && references && references.length > 0 && activeReferenceUrl) {
      const activeReferenceBaseUrl = activeReferenceUrl.split("?")[0];
      const reference = references.find((item) => item.fileUrl.split("?")[0] === activeReferenceBaseUrl);
      if (reference) {
        setSelectedReference(reference);
      }
    }
  }, [activeReferenceUrl, references, isReferencesListLoading]);

  const updateSelectedReference = useCallback(async () => {
    if (!isReferencesListLoading && !isDocumentLoading) {
      if (references && references.length > 0 && activeReferenceUrl) {
        const activeReferenceBaseUrl = activeReferenceUrl.split("?")[0];
        const reference = references.find((item) => item.fileUrl.split("?")[0] === activeReferenceBaseUrl);
        if (reference) {
          setFileId(reference.fileId);
          setViewError(false);
        } else {
          setViewError(true);
          console.warn("No matching reference found for URL:", activeReferenceBaseUrl, references);
        }
      }
    }
  }, [activeReferenceUrl, references, pageResetKey, isReferencesListLoading, isDocumentLoading]);

  useEffect(() => {
    if (activeReferenceUrl) {
      const fetchPdf = async () => {
        setIsDocumentLoading(true);
        setIsReferenceLoading(true);
        setViewError(false);
        setActiveParagraphReference(undefined);
        setFileId(undefined);
        clearPreviousBlob(modifiedPdfUrl);
        setModifiedPdfUrl(undefined);
        try {
          const response = await fetch(activeReferenceUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch the file: ${response.statusText}`);
          }
          const blob = await response.blob();
          setPdfBlob(blob);
          setIsDocumentLoading(false);
        } catch (error) {
          console.error("Failed to fetch PDF:", error);
          setIsDocumentLoading(false);
          setIsReferenceLoading(false);
        }
      };
      fetchPdf();
    }
  }, [activeReferenceUrl]);

  useEffect(() => {
    if (pdfBlob && !isDocumentLoading && !paragraphs) {
      const url = URL.createObjectURL(pdfBlob);
      setWarningMessage(undefined);
      setModifiedPdfUrl(url);
      setIsReferenceLoading(false);
    }
  }, [pdfBlob, isDocumentLoading, paragraphs]);

  useEffect(() => {
    if (activeReferenceUrl && !isReferencesListLoading && !isDocumentLoading) {
      updateSelectedReference();
    }
  }, [
    activeReferenceUrl,
    isReferencesListFetching,
    isReferencesListLoading,
    updateSelectedReference,
    pageResetKey,
    isDocumentLoading,
  ]);

  useEffect(() => {
    if (isLoading) {
      setActiveParagraphReference(undefined);
    }
    if (!isLoading && data && !isError) {
      if (data.pageNumber === pageNumber && data.fileId === fileId) {
        setActiveParagraphReference(data);
      }
    }
    if (isError) {
      setActiveParagraphReference(undefined);
    }
    if (!paragraphs) {
      setWarningMessage(undefined);
    }
  }, [data, isLoading, pageNumber, paragraphs]);

  // Convert inches to points (used in PDF coordinates)
  const convertInchesToPoints = (inches: number): number => inches * INCH_TO_POINTS;

  const calculateRectangleFromCoordinates = (coordinates: ICoordinates[]): IRectangle | undefined => {
    // Filter and convert coordinates to ensure valid numbers
    if (coordinates) {
      const validCoordinates = coordinates
        .map(({ x, y }) => ({
          x: parseFloat(x.toString()),
          y: parseFloat(y.toString()),
        }))
        .filter(({ x, y }) => !isNaN(x) && !isNaN(y));

      // Ensure exactly four valid coordinates are present
      if (validCoordinates.length !== 4) {
        console.error("Please provide exactly four sets of valid x, y coordinates.");
        return undefined;
      }

      // Convert coordinates from inches to points
      const convertedX = validCoordinates.map((coord) => convertInchesToPoints(coord.x));
      const convertedY = validCoordinates.map((coord) => convertInchesToPoints(coord.y));

      // Ensure all converted values are valid numbers
      if (convertedX.some(isNaN) || convertedY.some(isNaN)) {
        console.error("Invalid coordinate conversion: resulting in NaN values.");
        return undefined;
      }

      const minX = Math.min(...convertedX);
      const maxX = Math.max(...convertedX);
      const minY = Math.min(...convertedY);
      const maxY = Math.max(...convertedY);

      const rectangle: IRectangle = {
        x: minX, // Bottom-left x in points
        y: minY, // Bottom-left y in points
        width: maxX - minX, // Width in points based on x range
        height: maxY - minY, // Height in points based on y range
      };
      return rectangle;
    }
  };

  const getRectanglesArray = (paragraphs: IParagraph[]): IRectangle[] => {
    const rectangles: IRectangle[] = [];
    paragraphs.forEach((paragraph) => {
      const rectangle = calculateRectangleFromCoordinates(paragraph.boundingBox);
      if (rectangle) {
        rectangles.push(rectangle);
      }
    });

    return rectangles;
  };

  const clearPreviousBlob = (url: string | undefined) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  };

  const updatePage = async () => {
    if (pdfBlob) {
      setIsReferenceLoading(true);
      setWarningMessage(undefined);

      const array = await pdfBlob?.arrayBuffer();
      const doc = await PDFDocument.load(array, { ignoreEncryption: true });
      //if (!doc || isDocumentLoading) return;
      if (!doc.isEncrypted) {
        const pages = doc.getPages();
        const targetPage = pages[pageNumber - 1];
        const pageHeight = targetPage.getSize().height;

        if (activeParagraphReference?.paragraphs) {
          const rectangles = getRectanglesArray(activeParagraphReference?.paragraphs);
          if (rectangles) {
            rectangles.forEach((rectangle) => {
              if (rectangle) {
                targetPage.drawRectangle({
                  x: rectangle.x,
                  y: pageHeight - rectangle.y - rectangle.height,
                  width: rectangle.width,
                  height: rectangle.height,
                  borderWidth: 0.5,
                  borderColor: rgb(0.2157, 0.6863, 0.8824),
                  color: rgb(0.2157, 0.6863, 0.8824), // Blue rectanglergb(55, 175, 225)
                  opacity: 0.2,
                  borderOpacity: 0.6,
                });
              }
            });
          }
        }
        const pdfBytes = await doc.save();
        const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const urlToClear = modifiedPdfUrl;
        setModifiedPdfUrl(url);
        clearPreviousBlob(urlToClear);
        setIsReferenceLoading(false);
      } else {
        setWarningMessage("This document is encrypted, so highlight annotations may not display.");
        if (pdfBlob) {
          const url = URL.createObjectURL(pdfBlob);
          const urlToClear = modifiedPdfUrl;
          setModifiedPdfUrl(url);
          clearPreviousBlob(urlToClear);
        }
        if (!isDocumentLoading) {
          setIsReferenceLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (activeParagraphReference && !isDocumentLoading && paragraphs) {
      updatePage();
    }
  }, [activeParagraphReference, isDocumentLoading, isLoading, paragraphs]);

  useEffect(() => {
    if (newParagraph) {
      setIsReferenceLoading(true);
      setActiveParagraphReference(undefined);
    }

    setPageNumber(pageNum);
    setParagraphs(newParagraph);
  }, [pageNum, newParagraph, selectedReference?.fileId]);

  useEffect(() => {
    if (activeReferenceUrl) {
      setActiveConversationId(conversationId);
    }
  }, [activeReferenceUrl, conversationId, setActiveConversationId]);

  useEffect(() => {
    if (referencesList && !isReferencesListLoading) {
      setReferences(referencesList);
    } else {
      setReferences(undefined);
    }

    if (activeConversationId === undefined) {
      if (setIsCollapsedReference) {
        setIsCollapsedReference(true);
      }
      setReferences(undefined);
      setModifiedPdfUrl(undefined);
      setSelectedReference(undefined);
      setActiveParagraphReference(undefined);
      setParagraphs(undefined);
      setPageNumber(1);
      setViewError(false);
    }
  }, [
    activeConversationId,
    referencesList,
    setReferences,
    isConversationLoading,
    isReferencesListLoading,
    refetch,
    setIsCollapsedReference,
  ]);

  useEffect(() => {
    if (!isConversationLoading) {
      refetch();
    }
  }, [isConversationLoading, refetch]);

  useEffect(() => {
    if (!isCollapsedReference && conversationId) {
      setActiveConversationId(conversationId);
    }
    if (isCollapsedReference) {
      setSelectedReference(undefined);
      setViewError(false);
    }
  }, [conversationId, isCollapsedReference, setActiveConversationId, setSelectedReference]);

  useEffect(() => {
    if (conversationId === undefined) {
      setActiveConversationId(undefined);
    }
  }, [conversationId]);

  return {
    setActiveParagraphReference,
    setParagraphs,
    setFileId,
    pageNumber,
    setPageNumber,
    references,
    setReferences,
    selectedReference,
    setSelectedReference,
    activeConversationId,
    setActiveConversationId,
    referencesList,
    isReferencesListLoading,
    referencesError,
    modifiedPdfUrl,
    viewError,
    theme,
    matchDownMd,
    isReferenceLoading,
    isReferencesListFetching,
    currentPage,
    setCurrentPage,
    warningMessage,
  };
};
